import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["value", "onChange", "label", "fullWidth", "children", "required", "classes", "showItemIcon", "MenuPopperProps"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useEffect, useMemo, useState } from 'react';
import { Search, InputAdornment } from '@ellucian/react-design-system/core';
import { withEDSContext } from '../EDSContext/EDSContext';
import { default as withStyles } from '../styles';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import { spacing30 } from '../styles/tokens';
var styles = function styles() {
  return {
    positionStart: {
      marginRight: "" + spacing30
    }
  };
};

/**
 * Get the display text of the dropdown.
 */
function getDisplayText(value, valueMap) {
  var label;
  if (!isNil(value) && !isEmpty(valueMap)) {
    if (isNil(valueMap[value])) {
      console.error("Unknown value: " + value);
    } else {
      label = valueMap[value].props.label;
    }
  }
  return label;
}

/**
 * Get the icon for the dropdown typeahead item
 */
function getValueIcon(value, valueMap, classes) {
  if (value === void 0) {
    value = {};
  }
  if (valueMap === void 0) {
    valueMap = {};
  }
  var leftIconComponent = get(valueMap[value], 'props.LeftIconComponent');
  if (leftIconComponent) {
    return /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start",
      classes: {
        root: classes.positionStart
      }
    }, leftIconComponent);
  } else {
    return undefined;
  }
}

/**
 * A dropdown that support typeahead item search.
 *
 * @done true
 * @updated v7.13.0
 * @versionAdded v5.5.0
 * @examples
 *  DropdownTypeaheadExample
 *  DropdownTypeaheadRequiredExample
 *  DropdownTypeaheadIconExample
 */
var DropdownTypeahead = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var value = props.value,
    onChangeProp = props.onChange,
    label = props.label,
    fullWidth = props.fullWidth,
    children = props.children,
    required = props.required,
    classes = props.classes,
    showItemIcon = props.showItemIcon,
    MenuPopperProps = props.MenuPopperProps,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var _useState = useState(),
    dropdownItems = _useState[0],
    setDropdownItems = _useState[1];
  var _useState2 = useState({}),
    valueMap = _useState2[0],
    setValueMap = _useState2[1];
  var _useState3 = useState(),
    localValue = _useState3[0],
    setLocalValue = _useState3[1];
  var _useState4 = useState(),
    dropdownIcon = _useState4[0],
    setDropdownIcon = _useState4[1];
  var allChildren = useMemo(function () {
    return React.Children.toArray(children);
  }, [children]);
  useEffect(function () {
    setDropdownItems(allChildren);
    var map = allChildren.reduce(function (accumulator, val) {
      accumulator[val.props.value] = val;
      return accumulator;
    }, {});
    setValueMap(map);
  }, [allChildren]);
  useEffect(function () {
    setLocalValue(getDisplayText(value, valueMap));
    showItemIcon && setDropdownIcon(getValueIcon(value, valueMap, classes));
  }, [value, valueMap, classes, showItemIcon]);
  function filter(searchValue, item) {
    return !item.props.disabled && item.props.label.toLowerCase().startsWith(searchValue.toLowerCase());
  }
  useEffect(function () {
    if (isNil(onChangeProp)) {
      console.error('DropdownTypeahead: onChange prop is required');
    }
  }, [onChangeProp]);

  // TODO: We may need to optionally provide devs to implement this filter function
  var onFilter = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(searchValue) {
      var filterText, items;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            filterText = searchValue.trim();
            if (isEmpty(filterText)) {
              items = allChildren;
            } else {
              items = React.Children.toArray(children).filter(function (item) {
                return filter(filterText, item);
              });
            }
            setDropdownItems(items);
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function onFilter(_x) {
      return _ref.apply(this, arguments);
    };
  }();
  var onClear = function onClear(event) {
    onChangeProp && onChangeProp(undefined, event);
    showItemIcon && setDropdownIcon(undefined);
    setLocalValue(undefined);
    setDropdownItems(allChildren);
  };
  var onItemSelect = function onItemSelect(event, index) {
    var selectedItemValue = dropdownItems[index].props.value;
    onChangeProp && onChangeProp(selectedItemValue, event);
    setLocalValue(getDisplayText(selectedItemValue, valueMap));
    showItemIcon && setDropdownIcon(getValueIcon(selectedItemValue, valueMap, classes));
  };
  var onChange = function onChange(event) {
    setLocalValue(event.target.value);
    showItemIcon && setDropdownIcon(getValueIcon(event.target.value, valueMap, classes));

    // Pass undefined if text is cleared using backspace
    if (isNil(event.target.value) || isEmpty(event.target.value)) {
      onChangeProp && onChangeProp(undefined, event);
    }
  };
  return /*#__PURE__*/React.createElement(Search, _extends({
    onChange: onChange,
    ref: ref,
    onClear: onClear,
    value: localValue,
    label: required ? label + " *" : label,
    fullWidth: fullWidth,
    isDropdown: true,
    placeholder: "",
    typeaheadOptions: {
      loading: false,
      onFilter: onFilter,
      onItemSelect: onItemSelect,
      searchDelay: 0
    },
    dropdownItemIcon: showItemIcon ? dropdownIcon : undefined,
    TypeaheadPopperProps: MenuPopperProps
  }, rest), dropdownItems);
});
DropdownTypeahead.muiName = 'DropdownTypeahead';
DropdownTypeahead.displayName = 'DropdownTypeahead';
DropdownTypeahead.propTypes = {
  /**
   * One or more `DropdownTypeaheadItem components.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * The dropdown value. This corresponds to the `value` of the selected
   * `DropdownTypeaheadItem`.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  /**
   * Label that is attached to the `DropdownTypeahead`
   */
  label: PropTypes.string.isRequired,
  /**
   * Callback function fired when a dropdown item is selected.
   *
   * Signature:
   * ```
   * function(value: <any>, event: object) => void
   * ```
   * * `value`: The value that uniquely identifies the selected item. `undefined` if nothing is selected.
   * * `event`: The event source of the callback.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Event handler when the input gets the `blur` event.
   */
  onBlur: PropTypes.func,
  /**
   * If `true`, the dropdown will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the dropdown will display in an error state
   */
  error: PropTypes.bool,
  /**
   * If `true`, the dropdown will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * Text to be displayed by the FormHelperText component
   */
  helperText: PropTypes.string,
  /**
   * Id assigned to the input element and referenced by label element
   */
  id: PropTypes.string,
  /**
   * Marks the dropdown required
   */
  required: PropTypes.bool,
  /**
   * Size of the dropdown. Valid values are 'medium' (the default) and 'small'.
   */
  size: PropTypes.string,
  /**
   * If `true`, the dropdown will show the `LeftIconComponent` that corresponds with the `DropdownTypeaheadItem` in the input field when selected.
   */
  showItemIcon: PropTypes.bool,
  /**
   * Props to pass through to the menu's popper component. See `Popper` documentation for available
   * props.
   */
  MenuPopperProps: PropTypes.object
};
DropdownTypeahead.defaultProps = {
  required: false,
  disabled: false,
  fullWidth: false,
  error: false,
  showItemIcon: false,
  size: 'medium'
};
export default withEDSContext(withStyles(styles)(DropdownTypeahead));